
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { createBooking } from "@/lib/supabase";
import { FormValues } from "../types";
import { SubscriptionType } from "@/lib/supabase/types";
import { useAuth } from "@/lib/hooks/auth";

export const useBookingSubmission = (classId: string, verifiedGiftCardId: string | null, groupNameInputs: string[]) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Handle booking submission
  const bookingMutation = useMutation({
    mutationFn: createBooking,
    onSuccess: (result) => {
      if (result.success) {
        setBookingSuccess(true);
        
        // Store the booking ID for navigation to the manage page
        if (result.bookingId) {
          setBookingId(result.bookingId);
        }
        
        // Store payment status
        setPaymentStatus(result.paymentStatus);
        
        if (result.userId) {
          setCreatedUserId(result.userId);
          
          if (result.emailVerificationSent) {
            setVerificationEmailSent(true);
            toast.info("Account Created - Email Verification Required", {
              description: "Please check your inbox to verify your email address to complete your account creation.",
              duration: 8000,
            });
          } else {
            toast.success("Account Created", {
              description: "Your account has been created and you can now log in to view your bookings.",
            });
          }
        }
        
        // Show different toast based on payment status
        if (result.paymentStatus === 'pending') {
          toast.info("Booking Created - Payment Required", {
            description: "Your booking has been created. Please complete the payment to confirm your spot.",
            duration: 8000,
          });
        } else {
          toast.success("Booking Successful", {
            description: "Your class has been booked. We've sent a confirmation to your email.",
          });
        }
      } else {
        toast.error("Booking Failed", {
          description: result.error || "There was a problem booking your class. Please try again.",
        });
        setIsSubmitting(false);
      }
    },
    onError: (error: any) => {
      console.error("Booking error:", error);
      toast.error("Booking Failed", {
        description: error?.message || "There was a problem booking your class. Please try again.",
      });
      setIsSubmitting(false);
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });
  
  // Handle form submission
  const onSubmit = async (
    values: FormValues, 
    classDetails: any, 
    userSubscriptions: SubscriptionType[] = []
  ) => {
    if (!classDetails) {
      toast.error("Class Not Found", {
        description: "The class you're trying to book doesn't exist.",
      });
      return;
    }
    
    if (values.isGroupBooking && classDetails.availableSpots < values.groupSize) {
      toast.error("Not enough spots", {
        description: `There are only ${classDetails.availableSpots} spots available.`,
      });
      return;
    }
    
    if (values.isGroupBooking && values.useSubscription && values.useSubscription !== "pay_individually") {
      const subscription = userSubscriptions.find(sub => sub.id === values.useSubscription);
      if (subscription && subscription.remainingClasses < values.groupSize) {
        toast.error("Not enough classes in subscription", {
          description: `Your subscription only has ${subscription.remainingClasses} classes remaining.`,
        });
        return;
      }
    }
    
    if (values.useGiftCard && !verifiedGiftCardId) {
      toast.error("Gift Card Not Verified", {
        description: "Please verify your gift card before booking.",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Get user ID from either auth context or localStorage, but set to null if not available
    // This change ensures we don't send an invalid user ID to the database
    const userId = user?.id || localStorage.getItem('userId') || null;
    
    // Debug info before submitting
    console.log("Booking with user data:", {
      userId,
      createAccount: values.createAccount,
      hasPassword: !!values.password,
      passwordLength: values.password?.length
    });
    
    // Determine booking type and anticipate payment status
    const bookingType = values.useGiftCard ? 'gift_card' : 
                        (values.useSubscription && values.useSubscription !== 'pay_individually') ? 'subscription' : 
                        'single';
    
    // Calculate anticipated payment status
    const anticipatedPaymentStatus = (values.useGiftCard || 
                                     (values.useSubscription && values.useSubscription !== 'pay_individually')) ? 
                                     'paid' : 'pending';
    
    const bookingData = {
      classId,
      userId, // Now properly handling null case
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      createAccount: values.createAccount,
      password: values.createAccount ? values.password : undefined,
      skipEmailVerification: true, // Add this to indicate we want to skip email verification
      useSubscription: values.useSubscription || undefined,
      isGroupBooking: values.isGroupBooking,
      groupSize: values.isGroupBooking ? values.groupSize : 1,
      groupNames: values.isGroupBooking ? groupNameInputs : undefined,
      giftCardId: values.useGiftCard ? verifiedGiftCardId || undefined : undefined,
    };
    
    console.log("Submitting booking with data:", {
      ...bookingData,
      password: bookingData.password ? "[REDACTED]" : undefined,
      anticipatedPaymentStatus
    });
    
    try {
      // Use the mutation method
      bookingMutation.mutate(bookingData);
    } catch (error: any) {
      console.error("Booking submission error:", error);
      toast.error("Booking Failed", {
        description: error.message || "There was a problem booking your class. Please try again.",
      });
      setIsSubmitting(false);
    }
  };
  
  return {
    isSubmitting,
    bookingSuccess,
    createdUserId,
    verificationEmailSent,
    bookingId,
    paymentStatus,
    onSubmit
  };
};
